import Controller from '@ember/controller';
import { debounce, next } from '@ember/runloop';
import { action, computed } from '@ember/object';
import { readOnly, alias } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import {
  POSTING_APPLICATIONS_NAV_TITLES,
  POSTING_APPLICATIONS_NAV_TITLES_TO_ROUTES_MAP,
  POSTING_APPLICATIONS_PER_PAGE,
  POSTING_APPLICATIONS_SORT_API_ENUMS,
  POSTING_APPLICATIONS_SORT_OPTIONS,
  POSTING_SEARCH_CHARACTERS_BEFORE_REQUEST
} from 'sh-www/constants/routes/posting';
import { APPLICATION } from 'sh-www/models/application';
import { ICON_SORTS } from 'sh-www/constants/lsg';

class PostingBuyerController extends Controller {
  /**
   * Register services
   */
  @service router;
  @service('states/routes/posting') state;
  @service('state-managers/routes/posting') stateManager;

  /**
   * List of applications for the current view.
   *
   * @type {Ember.MutableArray<Application>}
   */
  @readOnly('state.applicationListItems') _applicationListItems;

  /**
   * Main assignment mode from our state.
   *
   * @type {Assignment}
   */
  @readOnly('state.assignment') _assignment;

  /**
   * True when the search component is activated
   *
   * NOTE: - This is an alias with the state manager, so you should NOT set this with a this.set()
   *
   * @type {Boolean}
   * @private
   */
  @alias('state.isSearchActivated') _isSearchActivated;

  /**
   * Counts for the different assignment statuses.  This will keep track of the old count, so
   * the counts don't disappear on the navigation b/t page loads.
   *
   * @type {Number}
   * @private
   */
  @readOnly('state.pendingCount') _pendingCount;
  @readOnly('state.hiredCount') _hiredCount;
  @readOnly('state.declinedCount') _declinedCount;

  /**
   * Do any of these statuses have a message that is read or unread
   *
   * @type {Boolean}
   * @private
   */
  @readOnly('state.pendingIsRead') _pendingIsRead;
  @readOnly('state.hiredIsRead') _hiredIsRead;
  @readOnly('state.declinedIsRead') _declinedIsRead;

  /**
   * True when we have a queued search to run.
   *
   * @type {Boolean}
   * @private
   */
  _hasQueuedSearch = false;

  /**
   * This will be true while we're loading all of the pages for the user.
   *
   * @type {Boolean}
   */
  @readOnly('state.isFilterFavorites') _isFilterFavorites;

  /**
   * True if we want to disable the filter sort component.
   *
   * @type {Boolean}
   */
  @readOnly('state.isLoadingResult') _isFilterSortDisabled;

  /**
   * True if we want to show loader
   *
   * @type {Boolean}
   * @private
   */
  @computed(
    'state.isLoadingAllPages',
    'state.isLoadingSearchResult',
    '_isSearchActivated',
    'state.isLoadingResult',
    'state.applicationListItems'
  )
  get _isShowSearchLoader() {
    if (this._isSearchActivated) {
      return this.state.isLoadingSearchResult;
    }

    if (this.state.isLoadingAllPages) {
      return false;
    }

    return this.state.isLoadingResult && !this.state.applicationListItems?.length;
  }

  /**
   * True when we want to show the filter sort in the view.
   *
   * @type {Boolean}
   */
  @computed(
    'state.isShowSelectAllOptions',
    '_isSearchActivated',
  )
  get _isShowFilterSort() {
    return !this.state.isShowSelectAllOptions && !this._isSearchActivated;
  }

  /**
   * Reset the controller back to the base state.
   *
   * @param {Boolean=} opt_isPreserveAssignmentState    set to true if you want to preserve the assignment
   *                                                    state and just reset the applicationList part.
   * @param {Boolean=} opt_isPreserveApplicationSortFilterStatus  set to true to preserve the application list
   *                                                              sorts, filter, and status.
   */
  resetController(opt_isPreserveAssignmentState, opt_isPreserveApplicationSortFilterStatus) {
    this.stateManager.resetStateManager(
      opt_isPreserveAssignmentState,
      opt_isPreserveApplicationSortFilterStatus,
      true,
    );
  }

  /**
   * Load the application for our view.
   *
   * @param {Boolean=} opt_isResetSecondarySort   True if we want to reset the secondary sort.
   * @private
   */
  _loadApplications(opt_isResetSecondarySort) {
    if (this.state.isResettingState || this.state.isLoadingFirstPage) {
      return;
    }

    const sortSelected = this._sortSelectedValue;
    const isFilterFavorites = this._isFilterFavorites;
    const secondarySort = opt_isResetSecondarySort
      ? ICON_SORTS.SORT_DESCENDING
      : this._secondarySort;

    this.stateManager.resetStateManager(true, true);
    this.stateManager.setNotFirstQuery();

    this.stateManager.loadApplications(
      this.state.assignment.id,
      this.state.currentApplicationFilterStatus,
      1,
      POSTING_APPLICATIONS_PER_PAGE,
      sortSelected,
      isFilterFavorites,
      secondarySort,
    );
  }

  /**
   * Array of objects that we'll feed into our lsg navigation component in the format:
   *
   * [{
   *  title: 'In Progress',
   *  count: 3,
   *  isActive: Function,
   *  isHighlight: Function,
   *  isHide: Function
   * },...]
   *
   * @type {Ember.MutableArray}
   * @private
   */
  @computed(
    'state.currentApplicationFilterStatus',
    '_pendingCount',
    '_pendingIsRead',
    '_hiredCount',
    '_hiredIsRead',
    '_declinedCount',
    '_declinedIsRead',
    '_isSearchActivated'
  )
  get _navItems() {
    const navItems = [];

    /**
     * @param {String} applicationStatus
     * @return {boolean}    true if the current status matches
     */
    const isStatus = (applicationStatus) => this.state.currentApplicationFilterStatus === applicationStatus;

    navItems.push({
      title: POSTING_APPLICATIONS_NAV_TITLES.PENDING,
      count: this._pendingCount,
      isActive: () => isStatus(APPLICATION.SIMPLE_STATUS.PENDING),
      isHighlight: () => !this._pendingIsRead,
    });

    navItems.push({
      title: POSTING_APPLICATIONS_NAV_TITLES.HIRED,
      count: this._hiredCount,
      isActive: () => isStatus(APPLICATION.SIMPLE_STATUS.HIRED),
      isHighlight: () => !this._hiredIsRead,
    });

    navItems.push({
      title: POSTING_APPLICATIONS_NAV_TITLES.DECLINED,
      count: this._declinedCount,
      isActive: () => isStatus(APPLICATION.SIMPLE_STATUS.DECLINED),
      isHighlight: () => !this._declinedIsRead,
    });

    return navItems;
  }

  /**
   * Observe both filters and update based on the changes
   */
  @action
  _observeFilterAndFavorites() {
    this._loadApplications(true);
  }

  /**
   * Watch the user's input in the search box, and update the search results from the server accordingly.
   */
  @action
  _observeSearchInput() {
    if (this.state.isResettingState || !this._isSearchActivated) {
      return;
    }

    const searchValue = this._searchTextValue;
    if (searchValue.length <= POSTING_SEARCH_CHARACTERS_BEFORE_REQUEST) {
      return;
    }

    debounce(this, this._runSearch, 500);
  }

  /**
   * Observe just the secondary sort.
   */
  @action
  _observeSecondarySort() {
    this._loadApplications();
  }

  /**
   * The current pagination model
   *
   * @type {Pagination}
   * @private
   */
  @readOnly('state.currentPagination') _pagination;

  /**
   * Run a search for the current state's search text value
   *
   * @private
   */
  _runSearch() {
    if (this.state.isLoadingSearchResult) {
      //we're already loading a result, so let's save this value, we only care about the latest value.
      this.set('_hasQueuedSearch', true);
      return;
    }

    const searchTextValue = this._searchTextValue;
    if (!searchTextValue) {
      this.set('_hasQueuedSearch', false);
      return;
    }

    this.stateManager.searchApplicationsForText(searchTextValue)
      .finally(() => {
        if (!this._hasQueuedSearch) {
          return;
        }

        this.set('_hasQueuedSearch', false);
        next(this, this._runSearch);
      });
  }

  /**
   * The search text value the user has typed in.
   *
   * @type {String}
   * @private
   */
  _searchTextValue = '';

  /**
   * The sort direction of the postings
   *
   * @type {String}   of type ICON_SORTS.*
   * @private
   */
  @alias('state.secondarySort') _secondarySort;

  /**
   * The options for the sort select in the lsg-select format.
   *
   * [{
   *  title: 'Last Activity',
   *  value: 'last-activity',
   *  selected: true/false
   * }...]
   *
   * @type {Ember.MutableArray}
   * @private
   */
  @computed('state.currentApplicationFilterStatus', '_sortSelectedValue')
  get _sortOptions() {
    const sortOptions = [];
    const status = this.state.currentApplicationFilterStatus;
    const selectedSortValue = this._sortSelectedValue;

    if (status === APPLICATION.SIMPLE_STATUS.PENDING) {
      sortOptions.push({
        title: POSTING_APPLICATIONS_SORT_OPTIONS.USER_SEARCH_RANK,
        value: POSTING_APPLICATIONS_SORT_API_ENUMS.USER_SEARCH_RANK,
        selected: selectedSortValue === POSTING_APPLICATIONS_SORT_API_ENUMS.BEST,
      });
    }

    sortOptions.push({
      title: POSTING_APPLICATIONS_SORT_OPTIONS.LAST_ACTIVITY,
      value: POSTING_APPLICATIONS_SORT_API_ENUMS.LAST_ACTIVITY,
      selected: selectedSortValue === POSTING_APPLICATIONS_SORT_API_ENUMS.LAST_ACTIVITY
    });

    if (status === APPLICATION.SIMPLE_STATUS.PENDING || status === APPLICATION.SIMPLE_STATUS.HIRED) {
      sortOptions.push({
        title: POSTING_APPLICATIONS_SORT_OPTIONS.NEEDS_ACTION,
        value: POSTING_APPLICATIONS_SORT_API_ENUMS.NEEDS_ACTION,
        selected: selectedSortValue === POSTING_APPLICATIONS_SORT_API_ENUMS.NEEDS_ACTION
      });
    }

    sortOptions.push({
      title: POSTING_APPLICATIONS_SORT_OPTIONS.UNREAD_MESSAGES,
      value: POSTING_APPLICATIONS_SORT_API_ENUMS.UNREAD_MESSAGES,
      selected: selectedSortValue === POSTING_APPLICATIONS_SORT_API_ENUMS.UNREAD_MESSAGES
    });

    if (status === APPLICATION.SIMPLE_STATUS.HIRED) {
      sortOptions.push({
        title: POSTING_APPLICATIONS_SORT_OPTIONS.DATE_STARTED,
        value: POSTING_APPLICATIONS_SORT_API_ENUMS.DATE_STARTED,
        selected: selectedSortValue === POSTING_APPLICATIONS_SORT_API_ENUMS.DATE_STARTED
      });
    }

    return sortOptions;
  }

  /**
   * The selected value of the sort dropdown.
   *
   * @type {String}   of type POSTING_APPLICATIONS_SORT_API_ENUMS.*
   * @private
   */
  @alias('state.sortSelectedValue') _sortSelectedValue;

  /**
   * The user has clicked to advance backward in the search results list
   *
   * @private
   */
  @action
  _advancePageBackwardClicked() {
    this.stateManager.decrementPage(
      this._sortSelectedValue,
      this._isFilterFavorites,
      this._secondarySort,
    );
  }

  /**
   * The user has clicked to advance forward in the search results list
   *
   * @private
   */
  @action
  _advancePageForwardClicked() {
    this.stateManager.incrementPage(
      this._sortSelectedValue,
      this._isFilterFavorites,
      this._secondarySort,
    );
  }

  @action
  toggleFilterFavorites() {
    this.set('state.isFilterFavorites', !this._isFilterFavorites);
  }

  /**
   * Called when one of the navItems is clicked on
   *
   * @param {String} title    from one of the navItems
   * @private
   */
  @action
  _onNavItemClicked(title) {
    const newRoute = POSTING_APPLICATIONS_NAV_TITLES_TO_ROUTES_MAP[title];
    if (this.router.currentPath === newRoute) {
      const currentStatus = this.state.currentApplicationFilterStatus;

      this.resetController(true);
      this.stateManager.loadApplications(this._assignment.id, currentStatus);
      return;
    }

    this.router.transitionTo(newRoute);
  }

  /**
   * Called when search is activated/deactivated.
   * @private
   */
  @action
  _onSearchToggleClicked() {
    if (!this._isSearchActivated) {
      this.set('_searchTextValue', '');
    }

    this.stateManager.searchToggleIsActivated();
  }
}

export default PostingBuyerController;
